import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Info, ScrollText, Settings2, ShieldAlert } from 'lucide-react';
import {
  fetchEinstellungen,
  saveEinstellungen,
  type EinstellungsAenderung,
} from '@/api/betrieb.api';

// ─── Einstellungen der Installation ─────────────────────────────────────────
//
// Getrennt in zwei Teile, und das mit Absicht:
//
//   änderbar  – liegt in der Datenbank, gilt sofort
//   fest      – kommt aus der Umgebung des Containers
//
// Das Zweite steht hier nur zum Nachschlagen. Ein Eingabefeld, dessen Inhalt
// beim nächsten Start überschrieben wird, wäre eine Lüge.
//
// Jeder Abschnitt speichert für sich. Ein Knopf für alles hieße, dass ein
// Tippfehler in der Anmeldesperre auch das Speichern der Aufbewahrungsfrist
// blockiert.

/** Ganze Zahl im erlaubten Bereich? */
function gueltig(text: string, min: number, max: number): boolean {
  const n = Number(text);
  return text.trim() !== '' && Number.isInteger(n) && n >= min && n <= max;
}

export function EinstellungenPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: fetchEinstellungen,
  });

  const [kontingent, setKontingent] = useState('');
  const [maxVersuche, setMaxVersuche] = useState('');
  const [fenster, setFenster] = useState('');
  const [aufbewahrung, setAufbewahrung] = useState('');

  // Erst wenn die Werte da sind – vorher stünde eine leere Zahl im Feld.
  useEffect(() => {
    if (!data) return;
    setKontingent(String(data.baumKontingent));
    setMaxVersuche(String(data.loginMaxVersuche));
    setFenster(String(data.loginFensterMinuten));
    setAufbewahrung(String(data.protokollAufbewahrungTage));
  }, [data]);

  const speichern = useMutation({
    mutationFn: (a: EinstellungsAenderung) => saveEinstellungen(a),
    onSuccess: (neu) => {
      queryClient.setQueryData(['admin', 'settings'], neu);
      queryClient.invalidateQueries({ queryKey: ['trees'] });
      toast.success('Einstellungen gespeichert.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) {
    return <div className="p-4 text-sm text-slate-500 sm:p-6">Einstellungen werden geladen…</div>;
  }

  if (isError || !data) {
    return (
      <div className="p-4 sm:p-6">
        <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error instanceof Error ? error.message : 'Einstellungen konnten nicht geladen werden.'}
        </p>
      </div>
    );
  }

  const kontingentUngueltig = !gueltig(kontingent, 0, 1000);
  const anmeldungUngueltig = !gueltig(maxVersuche, 1, 1000) || !gueltig(fenster, 1, 1440);
  const protokollUngueltig = !gueltig(aufbewahrung, 0, 3650);

  const kontingentGleich = kontingent === String(data.baumKontingent);
  const anmeldungGleich =
    maxVersuche === String(data.loginMaxVersuche) && fenster === String(data.loginFensterMinuten);
  const protokollGleich = aufbewahrung === String(data.protokollAufbewahrungTage);

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 sm:p-6">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900 sm:text-2xl">
          <Settings2 className="h-5 w-5 text-slate-400" aria-hidden="true" />
          Einstellungen
        </h1>
        <p className="mt-0.5 text-sm text-slate-500">
          Was diese Installation für alle Kunden vorgibt.
        </p>
      </header>

      {/* ─── Stammbäume ───────────────────────────────────────────────── */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="text-sm font-semibold text-slate-900">Stammbäume je Konto</h2>
        <p className="mt-1 text-sm leading-relaxed text-slate-600">
          Rückfallebene. Sie greift nur, wenn es überhaupt keinen Tarif gibt – im
          Regelfall entscheidet der <strong className="font-medium">Tarif</strong>{' '}
          des Kontos. Bestehende Bäume bleiben unberührt, auch wenn Sie den Wert
          senken.
        </p>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="kontingent" className="mb-1 block text-xs font-medium text-slate-700">
              Standard
            </label>
            <input
              id="kontingent"
              type="number"
              min={0}
              max={1000}
              value={kontingent}
              onChange={(e) => setKontingent(e.target.value)}
              className="w-32 rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <button
            onClick={() => speichern.mutate({ baumKontingent: Number(kontingent) })}
            disabled={speichern.isPending || kontingentGleich || kontingentUngueltig}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Speichern
          </button>
        </div>
        {kontingentUngueltig && kontingent.trim() !== '' && (
          <p className="mt-2 text-xs text-amber-600">Bitte eine ganze Zahl von 0 bis 1000.</p>
        )}
      </section>

      {/* ─── Anmeldung ────────────────────────────────────────────────── */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <ShieldAlert className="h-4 w-4 text-slate-400" aria-hidden="true" />
          Anmeldesperre
        </h2>
        <p className="mt-1 text-sm leading-relaxed text-slate-600">
          Wie oft von einer Adresse aus versucht werden darf, sich anzumelden,
          bevor weitere Versuche abgewiesen werden. Gezählt wird je Adresse, nicht
          je Konto – sonst könnte man ein fremdes Konto durch bloßes Falschraten
          aussperren.
        </p>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="versuche" className="mb-1 block text-xs font-medium text-slate-700">
              Versuche
            </label>
            <input
              id="versuche"
              type="number"
              min={1}
              max={1000}
              value={maxVersuche}
              onChange={(e) => setMaxVersuche(e.target.value)}
              className="w-28 rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <div>
            <label htmlFor="fenster" className="mb-1 block text-xs font-medium text-slate-700">
              Zeitfenster (Minuten)
            </label>
            <input
              id="fenster"
              type="number"
              min={1}
              max={1440}
              value={fenster}
              onChange={(e) => setFenster(e.target.value)}
              className="w-32 rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <button
            onClick={() =>
              speichern.mutate({
                loginMaxVersuche: Number(maxVersuche),
                loginFensterMinuten: Number(fenster),
              })
            }
            disabled={speichern.isPending || anmeldungGleich || anmeldungUngueltig}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Speichern
          </button>
        </div>
        <p className="mt-2 text-xs text-slate-400">
          Die Zahl der Versuche wirkt sofort. Das Zeitfenster wird beim Start
          festgelegt und gilt erst nach einem Neustart des Servers.
        </p>
        {anmeldungUngueltig && (
          <p className="mt-2 text-xs text-amber-600">
            Versuche: 1 bis 1000. Zeitfenster: 1 bis 1440 Minuten.
          </p>
        )}
      </section>

      {/* ─── Protokoll ────────────────────────────────────────────────── */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <ScrollText className="h-4 w-4 text-slate-400" aria-hidden="true" />
          Aufbewahrung des Protokolls
        </h2>
        <p className="mt-1 text-sm leading-relaxed text-slate-600">
          Ältere Einträge werden täglich gelöscht. Kurze Fristen sind
          datensparsam, lange helfen beim Nachvollziehen – was Sie behalten
          wollen, exportieren Sie vorher im Protokoll.
        </p>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="aufbewahrung" className="mb-1 block text-xs font-medium text-slate-700">
              Tage
            </label>
            <input
              id="aufbewahrung"
              type="number"
              min={0}
              max={3650}
              value={aufbewahrung}
              onChange={(e) => setAufbewahrung(e.target.value)}
              className="w-32 rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <button
            onClick={() => speichern.mutate({ protokollAufbewahrungTage: Number(aufbewahrung) })}
            disabled={speichern.isPending || protokollGleich || protokollUngueltig}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Speichern
          </button>
        </div>
        <p className="mt-2 text-xs text-slate-400">0 bedeutet: nichts wird gelöscht.</p>
      </section>

      {/* ─── Fest ─────────────────────────────────────────────────────── */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <Info className="h-4 w-4 text-slate-400" aria-hidden="true" />
          Aus der Umgebung
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Diese Werte stehen in der <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">.env</code>{' '}
          des Servers und ändern sich erst beim nächsten Start. Hier nur zum
          Nachschlagen.
        </p>

        <dl className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-[max-content,1fr]">
          <Wert label="Adresse der Anwendung" wert={data.umgebung.appUrl} />
          <Wert label="Domain der Konsole" wert={data.umgebung.adminDomain} />
          <Wert label="Ablage der Dateien" wert={data.umgebung.uploadDir} />
          <Wert
            label="Storage Box"
            wert={data.umgebung.hetznerKonfiguriert ? 'eingerichtet' : 'nicht eingerichtet'}
          />
          <Wert label="Vertraute Vermittler" wert={data.umgebung.trustProxy} />
        </dl>
      </section>
    </div>
  );
}

function Wert({ label, wert }: { label: string; wert: string | null }) {
  return (
    <>
      <dt className="text-sm text-slate-500">{label}</dt>
      <dd className="min-w-0 break-all font-mono text-sm text-slate-800">
        {wert ?? <span className="font-sans italic text-slate-400">nicht gesetzt</span>}
      </dd>
    </>
  );
}

export default EinstellungenPage;
